import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Users, Award, Shield, Layers, Calendar, Compass } from 'lucide-react';
import { motion } from 'framer-motion';

const COGNIA_LOGO = 'https://customer-assets.emergentagent.com/job_global-continuity/artifacts/xiv5benz_61af194567ecd52c38758fd7_Cognia%20glossy%20logo-p-500.png';

export default function ProofSection() {
  const { t, language } = useLanguage();

  const proofs = [
    {
      icon: Users,
      title: t.proof.small,
      desc: t.proof.smallDesc,
      accent: '#2D78F6',
    },
    {
      icon: Award,
      title: t.proof.teachers,
      desc: t.proof.teachersDesc,
      accent: '#66CA9D',
    },
    {
      icon: Shield,
      title: t.proof.accredited,
      desc: t.proof.accreditedDesc,
      accent: '#F8CD46',
    },
    {
      icon: Layers,
      title: t.proof.dual,
      desc: t.proof.dualDesc,
      accent: '#926EF7',
    },
    {
      icon: Calendar,
      title: language === 'fr' ? '32 Semaines de Cours' : '32 Weeks of Classes',
      desc: language === 'fr' 
        ? 'Une année académique complète de 32 semaines avec un programme structuré et progressif.'
        : 'A full 32-week academic year with a structured and progressive curriculum.',
      accent: '#EA5658',
    },
    {
      icon: Compass,
      title: language === 'fr' ? 'Orientation Supérieur Incluse' : 'University Guidance Included',
      desc: language === 'fr' 
        ? 'Accompagnement vers le supérieur (France et international) intégré dans les frais de scolarité.'
        : 'Higher education guidance (France and international) included in tuition fees.',
      accent: '#2D78F6',
    },
  ];

  return (
    <section
      data-testid="proof-section"
      className="bg-[#E7EAEF] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-[#2D78F6] text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t.proof.overline}
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#14274B] leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.proof.title}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-[#475569] leading-relaxed"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr'
              ? 'OFA France se distingue par la qualité de son enseignement, la taille réduite de ses classes et son accréditation COGNIA. Nos enseignants américains certifiés, notre programme structuré sur 32 semaines et notre accompagnement vers les études supérieures font d\'OFA France bien plus qu\'un cours d\'anglais — c\'est un véritable parcours académique international.'
              : 'OFA France stands out through the quality of its teaching, small class sizes and COGNIA accreditation. Our certified American teachers, our structured 32-week program and our higher education guidance make OFA France much more than an English class — it\'s a true international academic pathway.'}
          </motion.p>
        </div>

        {/* Proof Grid - Bento */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {proofs.map((proof, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              data-testid={`proof-card-${index}`}
              className={`bg-white p-8 md:p-10 border border-[#14274B]/5 ${
                index === 0 ? 'md:col-span-7' : ''
              } ${index === 1 ? 'md:col-span-5' : ''} ${
                index === 2 ? 'md:col-span-5' : ''
              } ${index === 3 ? 'md:col-span-7' : ''} ${
                index === 4 ? 'md:col-span-6' : ''
              } ${index === 5 ? 'md:col-span-6' : ''}`}
            >
              {/* Show Cognia logo for accreditation card (index 2) */}
              {index === 2 ? (
                <div className="mb-6">
                  <img
                    src={COGNIA_LOGO}
                    alt="COGNIA — Programme accrédité OFA France"
                    className="w-20 h-20 object-contain"
                  />
                </div>
              ) : (
                <div
                  className="w-14 h-14 flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${proof.accent}15` }}
                >
                  <proof.icon
                    className="w-7 h-7"
                    style={{ color: proof.accent }}
                  />
                </div>
              )}

              <h3
                className="text-xl md:text-2xl font-semibold text-[#14274B] mb-3"
                style={{ fontFamily: 'Gabarito, sans-serif' }}
              >
                {proof.title}
              </h3>

              <p
                className="text-[#475569] leading-relaxed"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {proof.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
